import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import Layout, { Overline } from "../components/Layout";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Motion";
import Icon from "../components/Icons";
import { IMAGES, SERVICES, DRONE_APPLICATIONS } from "../data/site";

export default function Services() {
  return (
    <Layout
      title="Services"
      description="Ranger development, Country assessments, restoration and native seed, cultural heritage, and strategy & governance — practical services that build lasting ranger capability around the world."
    >
      <PageHero
        overline="What We Do"
        titleLines={["Practical services", "for Country"]}
        subtitle="From ranger development to governance, our services build the capability that stays with your organisation."
        image={IMAGES.surveyor}
      />

      <section className="py-14 bg-warmwhite">
        <div className="mx-auto max-w-[88rem] px-6 lg:px-10">
          {SERVICES.map((s, i) => (
            <div key={s.slug} id={s.slug} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center py-16 lg:py-24 ${i > 0 ? "border-t border-charcoal/10" : ""}`}>
              <Reveal className={i % 2 ? "lg:order-2" : ""}>
                <div className="relative rounded-[2rem] overflow-hidden aspect-[4/3] group">
                  <img src={s.image} alt={s.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
                  <span className="absolute top-6 left-6 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-warmwhite/90 backdrop-blur text-eucalyptus text-2xl">
                    <Icon name={s.icon} />
                  </span>
                </div>
              </Reveal>
              <div className={i % 2 ? "lg:order-1" : ""}>
                <div className="text-eucalyptus mb-4"><Overline>{String(i + 1).padStart(2, "0")} · Service</Overline></div>
                <Reveal>
                  <h2 className="font-heading text-4xl md:text-5xl font-semibold tracking-tighter text-charcoal leading-[0.95]">{s.title}</h2>
                  <p className="mt-5 text-lg text-charcoal/70 leading-relaxed max-w-lg">{s.intro}</p>
                </Reveal>
                <Reveal delay={0.1}>
                  <ul className="mt-8 grid sm:grid-cols-2 gap-x-8 gap-y-3">
                    {s.items.map((it) => (
                      <li key={it} className="flex items-center gap-3 text-charcoal/80">
                        <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-eucalyptus/10 text-eucalyptus"><Check size={12} strokeWidth={3} /></span>
                        {it}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" data-testid={`service-cta-${s.slug}`} className="mt-8 inline-flex items-center gap-2 font-semibold text-eucalyptus hover:gap-3 transition-all">
                    Enquire about {s.title} <ArrowUpRight size={18} />
                  </Link>
                </Reveal>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Drone & Aerial Mapping */}
      <section className="relative bg-charcoal text-warmwhite overflow-hidden">
        <div className="absolute inset-0">
          <img src={IMAGES.droneCoast} alt="Aerial mapping of coastline" className="h-full w-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-charcoal via-charcoal/85 to-charcoal" />
          <div className="grain absolute inset-0 opacity-30" />
        </div>
        <div className="relative mx-auto max-w-[88rem] px-6 lg:px-10 py-24 lg:py-32">
          <div className="max-w-2xl">
            <div className="text-ochre mb-6"><Overline>Drone &amp; Aerial Mapping</Overline></div>
            <Reveal>
              <h2 className="font-heading text-4xl md:text-5xl lg:text-6xl font-semibold tracking-tighter leading-[0.95]">
                Seeing Country from above.
              </h2>
              <p className="mt-5 text-lg text-warmwhite/75 leading-relaxed">
                Drone mapping empowers ranger groups by streamlining environmental monitoring,
                cultural site preservation and coastal cleanup — reaching Country that is hard to
                walk, and watching how it changes over time.
              </p>
            </Reveal>
          </div>

          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-warmwhite/10 rounded-3xl overflow-hidden border border-warmwhite/10">
            {DRONE_APPLICATIONS.map((d, i) => (
              <Reveal key={d.title} delay={(i % 3) * 0.06}>
                <div className="group h-full bg-charcoal/60 backdrop-blur-sm p-8 hover:bg-eucalyptus transition-colors duration-300">
                  <span className="text-ochre group-hover:text-sandstone text-3xl transition-colors"><Icon name={d.icon} /></span>
                  <h3 className="mt-5 font-heading text-xl font-semibold tracking-tight">{d.title}</h3>
                  <p className="mt-2 text-warmwhite/65 group-hover:text-warmwhite/85 transition-colors leading-relaxed">{d.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Link to="/training?enquiry=Training" data-testid="drone-cta" className="mt-10 inline-flex items-center gap-2 rounded-full bg-ochre text-white px-7 py-4 font-semibold hover:bg-ochre/90 transition-colors">
            Explore drone training <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      <section className="py-24 bg-eucalyptus text-warmwhite relative overflow-hidden">
        <div className="grain absolute inset-0 opacity-30" />
        <div className="relative mx-auto max-w-[80rem] px-6 lg:px-10 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-light tracking-tight leading-tight max-w-3xl mx-auto text-balance">
            Not sure where to start? We will help you find the right first step.
          </h2>
          <Link to="/contact" data-testid="services-cta" className="mt-8 inline-flex items-center gap-2 rounded-full bg-warmwhite text-eucalyptus-deep px-7 py-4 font-semibold hover:bg-sandstone transition-colors">
            Start a conversation <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
